import React from "react";

type NodeOrText = string | React.ReactNode;

export interface ScheduleListProps {
  items: [NodeOrText, NodeOrText][]; // [label, value]
  hideRange?: [number, number]; // [startIndexToHide, trailingIndexWhenCollapsed]
  expandLabel: string;
  collapseLabel: string;
  onExpandClick?: VoidFunction;
  onCollapseClick?: VoidFunction;
}

export default function ScheduleList({
  items,
  hideRange,
  expandLabel,
  collapseLabel,
  onExpandClick,
  onCollapseClick,
}: ScheduleListProps) {
  const [expanded, setExpanded] = React.useState(false);

  const hasCollapsible =
    Array.isArray(hideRange) && items.length > (hideRange?.[0] ?? 0);

  const leadingCount = hasCollapsible
    ? Math.max(0, hideRange![0] - 1)
    : items.length;

  const leadingItems = items.slice(0, leadingCount);
  const trailingIndex = hasCollapsible
    ? Math.min(items.length - 1, hideRange![1])
    : -1;
  const trailingItem = trailingIndex >= 0 ? items[trailingIndex] : undefined;
  const expandableItems = hasCollapsible ? items.slice(leadingCount) : [];

  return (
    <div className="relative">
      {/* Vertical connecting line (logical inline start for RTL/LTR) */}
      {hasCollapsible && !expanded ? (
        // For collapsed state, use a more precise calculation
        <div
          className="absolute top-1.5 w-[1px] bg-gray-300"
          style={{
            insetInlineStart: "5.5px",
            height: `${(leadingItems.length + 1) * 36 - 6}px`, // leading items + trailing item, minus some offset
          }}
        />
      ) : (
        // For expanded state, use the original calculation
        <div
          className="absolute top-1.5 w-[1px] bg-gray-300"
          style={{
            insetInlineStart: "5.5px",
            height: `${(items.length - 1) * 36 - 6}px`, // all items minus first item margin, minus offset to stop at dot center
          }}
        />
      )}

      {/* Leading items */}
      {leadingItems.map(([label, value], index) => (
        <div
          key={`lead-${index}-${label}`}
          className={`relative flex items-center gap-3 ${index > 0 ? "mt-3" : ""}`}
        >
          <div
            className={`rounded-full z-10 ${
              index === 0
                ? "bg-purple-600 w-3 h-3"
                : "bg-[#DAE0EB] w-2 ms-[2px] h-2"
            }`}
          />
          <div
            className={`flex-1 flex justify-between ${index === 0 ? "text-[#16181D]" : "text-[#585B7A] text-sm"}`}
          >
            <span className={index === 0 ? "font-medium text-sm" : undefined}>
              {label}
            </span>
            <span className={index === 0 ? "font-medium text-sm" : undefined}>
              {value}
            </span>
          </div>
        </div>
      ))}

      {/* View all button when collapsed */}
      {hasCollapsible && !expanded && (
        <div className="relative flex items-center gap-3 mt-3">
          <div className="w-3 h-0 border-s-2 border-dashed border-gray-300 ms-1.5 z-10" />
          <button
            onClick={() => {
              setExpanded(true);

              if (onExpandClick) {
                onExpandClick();
              }
            }}
            className="text-purple-600 flex items-center gap-1 font-medium text-sm hover:text-purple-700 transition-colors"
          >
            {expandLabel}
            <span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.80407 4.51823L6.1002 7.79998L9.39632 4.49323L10.0002 5.08748L6.0877 8.99998L2.2002 5.11248L2.80407 4.51823Z"
                  fill="#9600F1"
                  stroke="#9600F1"
                  strokeWidth="0.375"
                />
              </svg>
            </span>
          </button>
        </div>
      )}

      {/* Trailing last item when collapsed */}
      {hasCollapsible && !expanded && trailingItem && (
        <div className="relative flex items-center gap-3 mt-3">
          <div className="w-2 h-2 bg-[#DAE0EB] ms-[2px] rounded-full z-10" />
          <div className="flex-1 flex justify-between text-[#585B7A]">
            <span className="text-sm">{trailingItem[0]}</span>
            <span className="text-sm">{trailingItem[1]}</span>
          </div>
        </div>
      )}

      {/* Expandable section */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out mt-3 ${
          expanded ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="space-y-3">
          {expandableItems.map(([label, value], idx) => (
            <div
              key={`exp-${idx}-${label}`}
              className={`relative flex items-center gap-3 transition-all duration-200 ease-out ${
                expanded
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-1 opacity-0"
              }`}
              style={{ transitionDelay: expanded ? `${idx * 30}ms` : "0ms" }}
            >
              <div className="w-2 h-2 bg-[#DAE0EB] ms-[2px] rounded-full z-10" />
              <div className="flex-1 flex justify-between text-[#585B7A]">
                <span className="text-sm">{label}</span>
                <span className="text-sm">{value}</span>
              </div>
            </div>
          ))}

          {hasCollapsible && (
            <div
              className={`flex items-center gap-2 transition-all duration-200 ${
                expanded
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-1 opacity-0"
              }`}
              style={{
                transitionDelay: expanded
                  ? `${expandableItems.length * 30 + 50}ms`
                  : "0ms",
              }}
            >
              <div className="w-3 h-0" />
              <button
                onClick={() => {
                  setExpanded(false);
                  if (onCollapseClick) {
                    onCollapseClick();
                  }
                }}
                className="text-purple-600 flex items-center gap-1 font-medium text-sm hover:text-purple-700 transition-colors"
              >
                {collapseLabel}
                <span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.60388 8.48175L5.9 5.2L9.19612 8.50675L9.8 7.9125L5.8875 4L2 7.8875L2.60388 8.48175Z"
                      fill="#9600F1"
                      stroke="#9600F1"
                      strokeWidth="0.375"
                    />
                  </svg>
                </span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
